import { pdf } from "@react-pdf/renderer";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import JobService from "../../api/JobService";
import PropertyService from "../../api/PropertyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { MaterialsList } from "../../components/MaterialsList/MaterialsList";
import deleteIcon from "../../components/Modal/delete-icon.png";
import Modal from "../../components/Modal/Modal";
import NoProductDocumentsModal from "../../components/Modal/NoProductDocumentsModal";
import { NoDocumentation } from "../../components/NoDocumentation/NoDocumentation";
import { MonitorListPDF } from "../../components/PDFDocument/MonitorListPDF";
import { PestsList } from "../../components/PestsList/PestsList";
import { PropertyAreaMonitors } from "../../components/PropertyAreaMonitors/PropertyAreaMonitors";
import { TimeTrackingOverview } from "../../components/TimeTrackingOverview/TimeTrackingOverview";
import { useDocumentation } from "../../hooks/useDocumentation";
import { useDownloadFiles } from "../../hooks/useDownloadFiles";
import { useScreenSize } from "../../hooks/useScreenSize";
import { useUnauthorizedAccess } from "../../hooks/useUnauthorizedAccess";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { splitDateTime } from "../../services/DateTimeService";
import { RootState } from "../../store";
import { appStateActions } from "../../store/app-state-slice";
import { appointmentsActions } from "../../store/appointments-slice";
import { checkIsRunning } from "../../store/clock-slice";
import { overviewAppointmentSliceActions } from "../../store/overview-appointment-slice";
import { servicedMonitorsActions } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/services-overview.module.css";
import { GroupedPest } from "../../types/pest";
import { calculateTotalTime } from "../../utils/calculateTotalTime";
import { mapPropertyAreasWithMonitors } from "../../utils/mapPropertyAreas";

export const ServicesOverviewReadonly = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDesktop } = useScreenSize();
  const { isCustomerAdmin } = useSelector((state: RootState) => state.user);

  const [jobId, setJobId] = useState("");
  const [propertyAreaMonitors, setPropertyAreaMonitors] = useState<any[]>([]);
  const [webProducts, setWebProducts] = useState<any>(null);
  const [sevdeskProducts, setSevdeskProducts] = useState<any>(null);
  const [products, setProducts] = useState<any>(null);
  const [infestationCountPerArea, setInfestationCountPerArea] = useState<{
    [key: string]: number;
  }>({});
  const [loading, setLoading] = useState(false);
  const [lastEventDate, setLastEventDate] = useState<string | null>(null);
  const [pests, setPests] = useState<GroupedPest[]>([]);
  const [selectedPest, setSelectedPest] = useState<GroupedPest | null>(null);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [monitorsList, setMonitorsList] = useState<any[]>([]);

  const printButtonRef = useRef<HTMLDivElement>(null);

  const user = useSelector((state: RootState) => state.user.currentUser.user);

  const servicesStep = useSelector(
    (state: RootState) => state.tabs.services.step
  );
  const isOnline = useSelector((state: RootState) => state.online.isOnline);

  const openAppointment = useSelector((state: RootState) => state.appointment);
  const appointment = useSelector(
    (state: RootState) => state.overviewAppointment
  );
  const serviceStarted = useSelector((state: RootState) =>
    checkIsRunning(state, openAppointment.id, user!.id)
  );
  const isEmpty = useSelector(
    (state: RootState) => state.technicianDocuments.empty
  );
  const isViewOnly = useSelector((state: RootState) => state.property.viewOnly);

  const isOtherAppointment = appointment.typeOfAppointment === "other";

  useUnauthorizedAccess(appointment);
  const { fetchDocumentation, requestLoading, noDocument, setNoDocument } =
    useDocumentation();

  const { closeModal } = useDownloadFiles();

  useEffect(() => {
    if (!showPrintOptions) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        printButtonRef.current &&
        !printButtonRef.current.contains(event.target as Node)
      ) {
        setShowPrintOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPrintOptions]);

  useEffect(() => {
    if (appointment.jobs.length > 0) {
      const copiedJobs = Array.from(appointment.jobs);
      const lastJobId = copiedJobs.pop();
      setJobId(lastJobId.id);
    }
  }, [appointment.jobs]);

  useEffect(() => {
    const fetchLastEvent = async () => {
      if (!appointment.id) return;

      const result = await calculateTotalTime(appointment.id);

      if (result.lastEvent?.createdAt) {
        const date = splitDateTime(result.lastEvent.createdAt).date;
        setLastEventDate(date);
      }
    };

    fetchLastEvent();
  }, [appointment.id]);

  const fetchPropertyAreaMonitors = useCallback(async () => {
    if (!appointment.propertyId || !appointment.id) {
      return;
    }
    if (appointment.propertyId) {
      try {
        dispatch(activeTabsActions.setMonitorsStep(1));
        setLoading(true);

        const [propertyResponse, propertyAreasResponse] = await Promise.all([
          PropertyService.GetProperty(appointment.propertyId),
          AppointmentService.GetAppointmentPropertyAreasWithVersions(
            appointment.id
          ),
        ]);

        const property = propertyResponse.data;
        const propertyAreasWithVersions = propertyAreasResponse.data;

        dispatch(appStateActions.addProperty(property));

        const propertyAreas = (propertyAreasWithVersions || []).filter(
          (area: any) =>
            Array.isArray(area.monitors) && area.monitors.length > 0
        );

        const propertyAreaIds = propertyAreas.map((area: any) => area.id);
        const monitorsPromises = propertyAreaIds.map((propertyAreaId: any) =>
          AppointmentService.GetMonitorsWithVersions(
            appointment.id,
            propertyAreaId
          )
        );

        const monitorsResponses = await Promise.all(monitorsPromises);

        const monitorsData = monitorsResponses.map((response) =>
          response.data.filter(
            (monitor: any) => monitor.versions && monitor.versions.length > 0
          )
        );

        setPropertyAreaMonitors(
          mapPropertyAreasWithMonitors(propertyAreas, monitorsData)
        );

        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    }
  }, [dispatch, appointment.propertyId, appointment.id]);

  const fetchGroupedMaterialsByJob = useCallback(async () => {
    if (jobId) {
      try {
        const { data } = await JobService.GetGroupedMaterialsByJobPDF(jobId);

        if (data.webProducts) {
          setWebProducts(
            data.webProducts.map((p: any) => ({
              id: p.item.productId,
              name: p.item.product.name,
              quantity: p.quantity,
              description: p.item.product.description,
              payExtraQuantity: p.payExtraQuantity ?? 0,
            }))
          );
        }

        if (data.sevdeskProducts) {
          setSevdeskProducts(
            data.sevdeskProducts.map((p: any) => ({
              id: p.item.id,
              name: p.item.name,
              quantity: p.quantity,
              description: p.description,
              payExtraQuantity: p.payExtraQuantity ?? 0,
            }))
          );
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [jobId]);

  const fetchGroupedEventsByJob = useCallback(async () => {
    if (jobId) {
      try {
        const { data } = await JobService.GetGroupedEventsByJob(jobId);

        const infestationCountMap: { [key: string]: number } = {};

        data.forEach((areaData: any) => {
          const areaLabel = areaData.label;
          const infestationEvents = areaData.events.infestation;

          if (infestationEvents) {
            infestationCountMap[areaLabel] = infestationEvents.length;
          } else {
            infestationCountMap[areaLabel] = 0;
          }
        });

        setInfestationCountPerArea(infestationCountMap);
      } catch (error) {
        console.log(error);
      }
    }
  }, [jobId]);

  const fetchGroupedPestsByJob = useCallback(async () => {
    if (!jobId) return;

    try {
      const { data } = await JobService.GetGroupedPestsByJob(jobId);
      setPests(data);
    } catch (error) {
      console.log(error);
    }
  }, [jobId]);

  const fetchMonitorListPDF = useCallback(async () => {
    if (!appointment.id) return;

    try {
      const { data } = await AppointmentService.GetMonitorListPDF(
        appointment.id
      );

      setMonitorsList(data);
    } catch (error) {
      console.log(error);
    }
  }, [appointment.id]);

  useEffect(() => {
    if (isOnline) {
      fetchGroupedMaterialsByJob();
      if (!isOtherAppointment) {
        fetchMonitorListPDF();
        fetchPropertyAreaMonitors();
        fetchGroupedEventsByJob();
        fetchGroupedPestsByJob();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchGroupedEventsByJob,
    fetchGroupedMaterialsByJob,
    fetchMonitorListPDF,
    fetchPropertyAreaMonitors,
    fetchGroupedPestsByJob,
    isOnline,
    appointment,
    isOtherAppointment,
  ]);

  useEffect(() => {
    const combinedProducts = [
      ...(webProducts || []),
      ...(sevdeskProducts || []),
    ];

    setProducts(combinedProducts);
  }, [webProducts, sevdeskProducts]);

  const handlePestClick = (pest: GroupedPest) => {
    if (isOtherAppointment) return;

    setSelectedPest(pest);
    dispatch(activeTabsActions.setServicesStep(3));
  };

  const handleBackButtonClicked = () => {
    if (isViewOnly && servicesStep === 3) {
      dispatch(activeTabsActions.setServicesStep(1));
      return;
    }

    if (isViewOnly) {
      navigate(`/properties/${appointment.propertyId}/viewonly`);
    }

    if (servicesStep === 1 && !isViewOnly) {
      if (isCustomerAdmin) {
        navigate("/");
      } else {
        dispatch(overviewAppointmentSliceActions.resetState());
        dispatch(servicedMonitorsActions.resetState());
        navigate(`/properties/${openAppointment.id}/show`);
      }
    } else {
      dispatch(activeTabsActions.setServicesStep(1));
    }
  };

  const fetchDocument = () => {
    fetchDocumentation(appointment.document?.url);
  };

  const generateMonitorListPDF = async () => {
    try {
      const document = (
        <MonitorListPDF
          appointment={appointment}
          propertyAreaMonitors={monitorsList}
        />
      );

      const blob = await pdf(document).toBlob();

      const url = URL.createObjectURL(blob);

      const link = window.document.createElement("a");
      link.href = url;
      link.download = "Monitorliste.pdf";
      link.click();

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Failed to generate monitor list PDF:", error);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(
        appointmentsActions.setAppointments({
          today: [],
          tomorrow: [],
          future: [],
        })
      );
    };
  });

  const propertyAreaMonitorsSortedByInfestationCount = useMemo(() => {
    return [...propertyAreaMonitors].sort((a, b) => {
      const infestationCountA = infestationCountPerArea[a.propertyArea] || 0;
      const infestationCountB = infestationCountPerArea[b.propertyArea] || 0;
      return infestationCountB - infestationCountA;
    });
  }, [propertyAreaMonitors, infestationCountPerArea]);

  return (
    <AppLayout hideHeader={!isCustomerAdmin}>
      <div>
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          handleFetchDocument={fetchDocument}
          options
          handleGenerateMonitorListPDF={generateMonitorListPDF}
        />
        <AppointmentDetails isServicesOverview lastEventDate={lastEventDate} />

        <Modal
          isOpen={isEmpty}
          message={t("NoInformation")}
          note={"NoAvailableDocuments"}
          iconType="noInfo"
          confirmText={"Back"}
          onConfirm={closeModal}
        />

        <NoProductDocumentsModal />

        {servicesStep === 1 && loading && <LoadingSpinner loading={loading} />}

        {servicesStep === 1 && !loading && jobId && (
          <div className={styles["content-wrapper"]}>
            <TimeTrackingOverview appointmentId={appointment.id} />

            <div className={styles.materials}>
              <CustomInput
                label={t("Remark")}
                placeholder=""
                hideTick
                initialValue={appointment.comment ?? ""}
                readonly
              />
            </div>
            <div className={styles["radio-sections"]}>
              {appointment.roomRelease != null &&
                appointment.roomRelease !== "" && (
                  <div
                    className={styles["radio-section"]}
                  >
                    <span>{t("RoomClearance")}</span>

                    <div
                      className={styles["radio-group--label"]}
                      style={{ marginTop: "15px" }}
                    >
                      <span>{appointment.roomRelease || "-"}</span>
                    </div>
                  </div>
                )}

              {appointment.measureRequired != null &&
                appointment.measureRequired !== "" && (
                  <div
                    className={styles["radio-section"]}
                  >
                    <span>{t("MeasuresRequired")}</span>

                    <div
                      className={styles["radio-group--label"]}
                      style={{ marginTop: "15px" }}
                    >
                      <span>{appointment.measureRequired || "-"}</span>
                    </div>
                  </div>
                )}
            </div>

            <div className={styles.materials}>
              <MaterialsList
                title={"Materialeinsatz"}
                data={products}
                readOnly={true}
              />
            </div>

            {!isOtherAppointment && (
              <>
                {pests.length > 0 && (
                  <div className={styles.materials}>
                    <PestsList
                      title="Schädlinge"
                      total={pests.reduce((sum, pest) => sum + pest.count, 0)}
                      items={pests.map((p) => ({
                        id: p.pestId,
                        label: p.pest,
                        count: p.count,
                      }))}
                      onItemClick={(item) => {
                        const pest = pests.find((p) => p.pestId === item.id)!;
                        handlePestClick(pest);
                      }}
                    />
                  </div>
                )}

                <div className={styles.monitors}>
                  <PropertyAreaMonitors
                    readonly
                    monitors={propertyAreaMonitorsSortedByInfestationCount}
                    startService={() => {}}
                    isServiceStarted={serviceStarted}
                    isServicesOverview={true}
                    jobId={jobId}
                    infestationCountPerArea={infestationCountPerArea}
                  />
                </div>
              </>
            )}
            {!isDesktop && (
              <FloatingButtons justify="right">
                <div
                  className={styles["print-button-container"]}
                  ref={printButtonRef}
                >
                  {showPrintOptions && (
                    <div className={styles.options}>
                      <span className={styles.option} onClick={fetchDocument}>
                        Leistungsnachweis
                      </span>

                      <span
                        className={styles.option}
                        onClick={generateMonitorListPDF}
                      >
                        Monitorliste
                      </span>
                    </div>
                  )}

                  {showPrintOptions ? (
                    <button
                      onClick={() => setShowPrintOptions((prev) => !prev)}
                      className={styles["delete-button"]}
                    >
                      <img
                        src={deleteIcon}
                        alt="Delete button icon"
                        style={{ width: "12px", height: "12px" }}
                      />
                    </button>
                  ) : (
                    <CustomButton
                      color="gray"
                      icon={showPrintOptions ? "delete" : "printer"}
                      loading={requestLoading}
                      width={48}
                      onClick={() => setShowPrintOptions((prev) => !prev)}
                    />
                  )}
                </div>
              </FloatingButtons>
            )}
          </div>
        )}

        {!loading && !jobId && (
          <>
            <NoDocumentation />

            <FloatingButtons>
              <CustomButton
                color="gray"
                {...(!isDesktop
                  ? { icon: "leftarrow", iconPosition: "left" }
                  : {})}
                width={70}
                onClick={handleBackButtonClicked}
              >
                {t("Back to overview")}
              </CustomButton>
            </FloatingButtons>
          </>
        )}

        {!isOtherAppointment && servicesStep === 3 && (
          <div className={styles["content-wrapper"]}>
            <PestsList
              title={"Monitor"}
              items={
                selectedPest?.monitors.map((m) => ({
                  id: m.id,
                  label: m.monitorType.label + " " + m.position,
                  count: m.count,
                  location: m.location,
                })) ?? []
              }
            />

            <FloatingButtons>
              <CustomButton
                color="gray"
                icon="leftarrow"
                iconPosition="left"
                width={70}
                onClick={handleBackButtonClicked}
              >
                {t("Back to overview")}
              </CustomButton>
            </FloatingButtons>
          </div>
        )}
      </div>

      <Modal
        isOpen={noDocument}
        message={t("NoDocument")}
        confirmText="OK"
        iconType="alert"
        onConfirm={() => setNoDocument(false)}
      />
    </AppLayout>
  );
};
